import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { basicInfoQuestions, withholdingExemptionQuestions, filingStatusQuestion } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function MassachusettsForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const withholdingForm = useRef();
  const withholdingExemptionInitQ = getInitialQuestions(withholdingExemptionQuestions);
  const [withholdingExemption, setWithholdingExemption] = useState(withholdingExemptionInitQ);

  const filingStatusForm = useRef();
  const filingStatusInitQ = getInitialQuestions(filingStatusQuestion);
  const [filingStatus, setFilingStatus] = useState(filingStatusInitQ);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
    </Flex>
  );

  const renderWithholdingQuestions = (isReviewStep) => (
    <Flex column>
      <Text mb="10px">Select the options that apply to you to claim your withholding exemptions.</Text>
      <FormConsumer
        questions={withholdingExemption}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = withholdingExemption.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWithholdingExemption(questions);
        }}
        ref={withholdingForm}
      />
      <Text mb="10px" style={{ fontWeight: '600', fontSize: '15px' }}>
        Filing status
      </Text>
      <FormConsumer
        questions={filingStatus}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = filingStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setFilingStatus(questions);
        }}
        ref={filingStatusForm}
      />
    </Flex>
  );

  const calculateNumExemptions = () => {
    let line1;
    const personalExemption = withholdingExemption[0].answer.answer.personal_exemptions;
    if (personalExemption === 'for_self') {
      line1 = 1;
    } else if (personalExemption === 'self_age_65') {
      line1 = 2;
    } else {
      line1 = 0;
    }

    let line2;
    const spouseExemption = withholdingExemption[1].answer.answer.spouse_exemptions;
    if (spouseExemption === 'married') {
      line2 = 4;
    } else if (spouseExemption === 'spouse_age_65') {
      line2 = 5;
    } else {
      line2 = 0;
    }

    const line3 = parseInt(withholdingExemption[2].answer.answer.dependents) || 0;

    return line1 + line2 + line3;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderWithholdingQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (withholdingForm && withholdingForm.current) {
              withholdingForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (filingStatusForm && filingStatusForm.current) {
              filingStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const withholdingExemptionData = withholdingExemption.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const filingStatusData = filingStatus.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              const calculatedExemptionData = { num_exemptions: calculateNumExemptions() };

              const data = {
                ...basicInfoData,
                ...withholdingExemptionData,
                ...filingStatusData,
                ...calculatedExemptionData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderWithholdingQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default MassachusettsForm;
